import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import Image from 'next/image'
import {useSearchParams} from 'next/navigation'
import {useLinkResolver} from 'prismicio'
import type {
  ButtonDocument,
  HeroV2SliceBentoDynamicPrimary,
  HeroV2SliceBentoDynamicPrimaryGridAssetsItem,
  HeroV2SliceBentoDynamicPrimaryHeadingsItem,
  HeroV2SliceBentoDynamicPrimarySubtextItem,
  HeroV2SliceBentoGridReskinItem,
} from 'prismicio-types'
import type {PropsWithChildren} from 'react'
import {useState} from 'react'
import {useEffectOnce} from 'react-use'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData, getPersonalisedContent} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {Tag} from 'src/common/Tag'
import {RatingsSection} from './RatingsSection'
import {useUtmTermTracker} from './useUtmTermTracker'

type BentoGridDynamicHeaderProps = {
  slice: {
    primary: HeroV2SliceBentoDynamicPrimary
    items: HeroV2SliceBentoGridReskinItem[]
    slice_type: string
    variation: string
  }
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const BentoGridDynamicHeader = ({slice, pageUid, componentIndex, buttonsData}: BentoGridDynamicHeaderProps) => {
  const {linkResolver} = useLinkResolver()
  const search = useSearchParams()
  const utm_term = search.get('utm_term') ?? ''
  let sanitizedHeading = ''

  const rawHeading = getPersonalisedContent<HeroV2SliceBentoDynamicPrimaryHeadingsItem>(
    slice.primary.headings,
    utm_term || '',
  )

  sanitizedHeading = sanitizeHtml(rawHeading?.heading || '')

  const subtext = getPersonalisedContent<HeroV2SliceBentoDynamicPrimarySubtextItem>(
    slice.primary.subtext,
    utm_term || '',
  )

  const assets = getPersonalisedContent<HeroV2SliceBentoDynamicPrimaryGridAssetsItem>(
    slice.primary.grid_assets,
    utm_term || '',
  )

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
  })

  useUtmTermTracker({
    pageUid,
    componentIndex,
    heading: sanitizedHeading,
    subtext: subtext?.subtext || '',
    images: assets,
  })

  return (
    <Container
      backgroundColor={slice.primary.background_color!}
      className="flex min-h-[calc(100vh+60px)] flex-col gap-12 !pb-12 !pt-[137px] md:gap-[60px] md:!pb-16"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      extraPaddingTop
    >
      <DynamicWrapper>
        <div className="mx-auto grid w-full max-w-[1200px] scroll-mt-16 grid-cols-4 gap-4 gap-y-12 px-5 md:grid-cols-12 md:gap-x-6 md:px-10 lg:px-5 xl:px-0">
          <div className="col-span-full flex flex-col gap-y-18 md:grid md:grid-cols-subgrid md:gap-y-0">
            <div
              className="max-md:grid-base grid items-center gap-y-4 md:col-span-5 md:grid-cols-subgrid md:items-start md:gap-y-6 md:self-center"
              data-aos="fade-up"
              data-aos-offset="0"
            >
              <div className="col-span-4 grid grid-cols-subgrid gap-4 text-center md:col-span-full md:grid-cols-subgrid md:gap-6 md:text-start">
                <h1
                  className="text-h2 col-span-full text-black lg:text-[48px] lg:leading-14"
                  dangerouslySetInnerHTML={{__html: sanitizedHeading}}
                />
                <p className="col-span-4 text-[18px] font-medium leading-6 tracking-[.18px] text-grey-550">
                  {subtext?.subtext || ''}
                </p>
              </div>
              <div className="col-span-full flex flex-wrap justify-center gap-2 md:justify-start md:pt-2">
                {primaryButtonData && (
                  <ButtonV2
                    variant="primary"
                    href={linkResolver(primaryButtonData.link, 'primary-button-hero')}
                    trackingData={{
                      mktgId: primaryButtonData.marketing_id || '',
                      location: `component-${componentIndex}-hero`,
                      dataType: primaryButtonData.intent || 'primary-button',
                      pageName: pageUid,
                      label: primaryButtonData.label!,
                    }}
                    id={`component-${componentIndex}-hero-1`}
                    className="uppercase"
                  >
                    {primaryButtonData.label}
                  </ButtonV2>
                )}
                {secondaryButtonData && (
                  <ButtonV2
                    href={linkResolver(secondaryButtonData.link, 'secondary-button-hero')}
                    variant="secondary"
                    trackingData={{
                      mktgId: secondaryButtonData.marketing_id || '',
                      location: `component-${componentIndex}-hero`,
                      dataType: secondaryButtonData.intent || 'secondary-button',
                      pageName: pageUid,
                      label: secondaryButtonData.label!,
                    }}
                    id={`component-${componentIndex}-hero-2`}
                    className="uppercase text-black"
                  >
                    {secondaryButtonData?.label}
                  </ButtonV2>
                )}
              </div>
              {isFilled.keyText(slice.primary.tag) && <Tag label={slice.primary.tag} />}
              {isFilled.group(slice.primary.ratings) && (
                <div
                  className={cn('col-span-full flex gap-3', {
                    'row-start-1 justify-center md:justify-start': slice.primary.ratings_on_top,
                  })}
                >
                  <RatingsSection
                    ratings={slice.primary.ratings}
                    type={slice.primary.ratings_type}
                    centered={false}
                    iconContainerColor="Moss Black"
                  />
                </div>
              )}
              {isFilled.keyText(slice.primary.disclaimer) && (
                <p className="text-body-s col-span-full text-grey-300">{slice.primary.disclaimer}</p>
              )}
            </div>
            <div className="max-md:grid-base grid aspect-square gap-y-4 self-center md:col-span-6 md:col-start-7 md:grid-cols-subgrid md:gap-y-6">
              <div className="col-span-2 aspect-square size-full self-center overflow-hidden rounded-lg md:col-span-3 lg:max-h-[282px]">
                <Image
                  src={assets?.top_left.url || ''}
                  alt=" "
                  width={700}
                  height={700}
                  className="size-full"
                  priority
                />
              </div>
              <div className="col-span-2 aspect-square size-full self-center overflow-hidden rounded-lg md:col-span-3 lg:max-h-[282px]">
                <Image
                  src={assets?.top_right.url || ''}
                  alt=" "
                  width={700}
                  height={700}
                  className="size-full"
                  priority
                />
              </div>
              <div className="col-span-full aspect-[2/1] size-full self-center overflow-hidden rounded-lg lg:max-h-[282px]">
                <Image src={assets?.bottom.url || ''} alt=" " width={700} height={700} className="size-full" priority />
              </div>
            </div>
          </div>
          {!!slice.items.length && (
            <div className="col-span-full flex flex-col gap-6 md:flex-row md:gap-20">
              {slice.items.map((item, index) => (
                <div className="flex w-full flex-col items-center gap-3 md:items-start" key={item.title! + index}>
                  <div className="relative flex size-8 items-center justify-center rounded-md bg-moss-white">
                    <PrismicNextImage field={item.icon} className="size-4 object-cover" />
                    <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <h3 className="text-h5 text-center leading-7 text-black md:text-start">{item.title}</h3>
                    <p className="text-center text-[15px] leading-[22px] text-grey-450 md:text-start">{item.subtext}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DynamicWrapper>
    </Container>
  )
}

const DynamicWrapper = ({children}: PropsWithChildren) => {
  const [mounted, setMounted] = useState(false)

  useEffectOnce(() => {
    setMounted(true)
  })

  if (!mounted) {
    return null
  }

  return children
}
